// Notes:

// appearance="contained" is the default (from defaultProps in fab.ts), so it's only necessary to specify outlined or soft.
// color accepts default, inherit, primary, secondary, info, success, warning, error — all wired up in the soft branch of colorStyle/neutralStyle.
// For soft + primary, the background uses the exact theme.palette.border.cyan token from Figma; every other color falls back to alpha(theme.palette[color].main, 0.16).
// size is small | medium | large (MUI's native Fab sizes — these map 1:1 to Figma's S/M/L).

import { alpha, type Theme } from '@mui/material/styles';
import { type FabProps, fabClasses } from '@mui/material/Fab';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

declare module '@mui/material/Fab' {
  interface FabPropsColorOverrides {
    info: true;
    success: true;
    warning: true;
    error: true;
  }
  // FabProps is a type alias (not an interface), so it can't be augmented directly —
  // FabOwnProps is the actual interface that feeds into it.
  interface FabOwnProps {
    appearance?: 'contained' | 'outlined' | 'soft';
  }
}

// ----------------------------------------------------------------------

export function fab(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: FabProps) => {
    const color = ownerState.color ?? 'default';
    const appearance = ownerState.appearance ?? 'contained';
    const size = ownerState.size ?? 'large';

    const containedAppearance = appearance === 'contained';
    const outlinedAppearance = appearance === 'outlined';
    const softAppearance = appearance === 'soft';

    const defaultColor = color === 'default';
    const inheritColor = color === 'inherit';

    const typographyStyle = {
      ...(size === 'small' && { fontWeight: 500, fontSize: 13, lineHeight: '22px' }),
      ...(size === 'medium' && { fontWeight: 500, fontSize: 14, lineHeight: '24px' }),
      ...(size === 'large' && { fontWeight: 600, fontSize: 15, lineHeight: '26px' })
    };

    const baseStyle = {
      borderRadius: 12,
      boxShadow: 'none',
      ...typographyStyle,
      '&:active': {
        boxShadow: 'none'
      },
      '& svg': {
        width: 24,
        height: 24
      },
      [`&.${fabClasses.extended}`]: {
        minWidth: 40,
        gap: theme.spacing(1)
      }
    };

    // Outlined and soft look identical for the neutral (default/inherit) colors,
    // so both share this block.
    const neutralOutlined = {
      backgroundColor: 'transparent',
      border: `1px solid ${theme.palette.text.disabled}`,
      color: theme.palette.text.tertiary,
      '&:hover': {
        backgroundColor: theme.palette.text.disabled,
        border: `1.5px solid ${theme.palette.text.tertiary}`
      }
    };

    const neutralStyle = {
      ...(defaultColor && {
        ...(containedAppearance && {
          backgroundColor: theme.palette.border.subtle,
          boxShadow: theme.customShadows.z8,
          '&:hover': { backgroundColor: theme.palette.text.disabled, boxShadow: 'none' }
        }),
        ...(outlinedAppearance && neutralOutlined),
        ...(softAppearance && {
          backgroundColor: theme.palette.border.subtle,
          color: theme.palette.text.tertiary,
          '&:hover': { backgroundColor: theme.palette.text.disabled }
        })
      }),
      ...(inheritColor && {
        ...(containedAppearance && {
          backgroundColor: theme.palette.text.tertiary,
          color: theme.palette.common.white,
          boxShadow: theme.customShadows.z8,
          '&:hover': { backgroundColor: theme.palette.text.secondary, boxShadow: 'none' }
        }),
        ...(outlinedAppearance && neutralOutlined),
        ...(softAppearance && {
          backgroundColor: theme.palette.text.disabled,
          color: theme.palette.common.white,
          '&:hover': { backgroundColor: theme.palette.text.secondary }
        })
      })
    };

    const colorStyle = COLORS.map((namedColor) => ({
      ...(color === namedColor && {
        ...(containedAppearance && {
          backgroundColor: theme.palette[namedColor].main,
          color: theme.palette.common.white,
          boxShadow: theme.customShadows[namedColor],
          '&:hover': { backgroundColor: theme.palette[namedColor].dark, boxShadow: 'none' }
        }),
        ...(outlinedAppearance && {
          backgroundColor: 'transparent',
          border: `1px solid ${theme.palette[namedColor].main}`,
          color: theme.palette[namedColor].main,
          '&:hover': {
            backgroundColor: theme.palette[namedColor].main,
            border: `1.5px solid ${theme.palette[namedColor].main}`,
            color: theme.palette.common.white
          }
        }),
        ...(softAppearance && {
          backgroundColor:
            namedColor === 'primary' ? theme.palette.border.cyan : alpha(theme.palette[namedColor].main, 0.16),
          color: theme.palette[namedColor][lightMode ? 'dark' : 'light'],
          '&:hover': { backgroundColor: theme.palette[namedColor].main, color: theme.palette.common.white }
        })
      })
    }));

    const disabledState = {
      [`&.${fabClasses.disabled}`]: {
        boxShadow: 'none',
        ...(containedAppearance && { backgroundColor: theme.palette.text.disabled }),
        ...(outlinedAppearance && { backgroundColor: 'transparent', borderColor: theme.palette.text.disabled }),
        ...(softAppearance && { backgroundColor: theme.palette.text.disabled })
      }
    };

    return [baseStyle, neutralStyle, ...colorStyle, disabledState];
  };

  return {
    MuiFab: {
      defaultProps: {
        appearance: 'contained',
        color: 'default'
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: FabProps }) => rootStyles(ownerState)
      }
    }
  };
}
